import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
    useConvertCurrency,
    useStoredRates,
    useExchangeRateCircuitState,
    useResetExchangeRateCircuit,
} from '@/api/hooks/useExchangeRates';
import type {
    ExchangeRateConvertResponse,
    CircuitBreakerState,
    StoredRateItem,
    RateSource,
} from '@/api/types';

function SourceBadge({ source, fallbackDate }: { source: RateSource | null; fallbackDate?: string | null }) {
    if (!source) return null;

    const styles: Record<RateSource, string> = {
        database: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        api: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        calculated: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        fallback: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        identity: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
    };

    const labels: Record<RateSource, string> = {
        database: 'DB',
        api: 'API',
        calculated: 'Calc',
        fallback: 'Fallback',
        identity: '1:1',
    };

    return (
        <span className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium ${styles[source]}`}>
            {labels[source]}
            {source === 'fallback' && fallbackDate && (
                <span className="text-xs opacity-75">({fallbackDate})</span>
            )}
        </span>
    );
}

function StateIndicator({ state }: { state: CircuitBreakerState }) {
    const colors = {
        closed: 'bg-green-500',
        open: 'bg-red-500',
        half_open: 'bg-yellow-500',
    };

    const labels = {
        closed: 'Closed',
        open: 'Open',
        half_open: 'Half Open',
    };

    return (
        <div className="flex items-center gap-2">
            <span className={`inline-block h-3 w-3 rounded-full ${colors[state]}`} />
            <span className="font-medium">{labels[state]}</span>
        </div>
    );
}

function ConversionResultCard({ result }: { result: ExchangeRateConvertResponse }) {
    return (
        <div
            className={`rounded-lg border p-4 ${
                result.success
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                    : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
            }`}
        >
            <div className="mb-2 flex items-center justify-between">
                <span
                    className={`font-semibold ${
                        result.success
                            ? 'text-green-700 dark:text-green-400'
                            : 'text-red-700 dark:text-red-400'
                    }`}
                >
                    {result.success ? 'Success' : 'Failed'}
                </span>
                <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                    {result.duration_ms}ms
                </span>
            </div>
            {result.error && (
                <div className="mb-2">
                    <span className="text-sm text-red-600 dark:text-red-400">
                        {result.error}
                    </span>
                </div>
            )}
            {result.data && (
                <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Original:</span>
                        <span className="font-mono">
                            {result.data.originalAmount} {result.data.from}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Converted:</span>
                        <span className="font-mono">
                            {result.data.convertedAmount?.toFixed(4) ?? 'N/A'} {result.data.to}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Rate:</span>
                        <span className="font-mono">{result.data.rate?.toFixed(6) ?? 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Date:</span>
                        <span className="font-mono">{result.data.date}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400">Source:</span>
                        <SourceBadge source={result.data.source} fallbackDate={result.data.fallbackDate} />
                    </div>
                </div>
            )}
        </div>
    );
}

function StoredRatesTable({ items }: { items: StoredRateItem[] }) {
    if (items.length === 0) {
        return (
            <p className="text-gray-500 dark:text-gray-400">
                No stored rates for this date yet.
            </p>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">
                            Source
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">
                            Target
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">
                            Rate
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => (
                        <tr
                            key={item.id}
                            className="border-b border-gray-100 dark:border-gray-800"
                        >
                            <td className="px-3 py-2 font-mono">{item.source}</td>
                            <td className="px-3 py-2 font-mono">{item.target}</td>
                            <td className="px-3 py-2 text-right font-mono">
                                {item.rate.toFixed(6)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Get yesterday's date in Y-m-d format (UTC).
 */
function getYesterday(): string {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - 1);
    return date.toISOString().split('T')[0] ?? '';
}

export function ExchangeRatesPage() {
    const [amount, setAmount] = useState('100');
    const [fromCurrency, setFromCurrency] = useState('USD');
    const [toCurrency, setToCurrency] = useState('EUR');
    const [selectedDate, setSelectedDate] = useState(getYesterday());
    const [conversionHistory, setConversionHistory] = useState<ExchangeRateConvertResponse[]>(
        [],
    );

    const { data: storedRates, isLoading: isLoadingRates } = useStoredRates();
    const { data: circuitState, isLoading: isLoadingCircuit } = useExchangeRateCircuitState();
    const convertMutation = useConvertCurrency();
    const resetMutation = useResetExchangeRateCircuit();

    const handleConvert = async () => {
        const result = await convertMutation.mutateAsync({
            amount: parseFloat(amount) || 100,
            from: fromCurrency.toUpperCase(),
            to: toCurrency.toUpperCase(),
            date: selectedDate,
        });
        setConversionHistory((prev) => [result, ...prev].slice(0, 20));
    };

    const handleReset = async () => {
        await resetMutation.mutateAsync();
    };

    const quickConversions = [
        { from: 'USD', to: 'EUR' },
        { from: 'EUR', to: 'GBP' },
        { from: 'USD', to: 'JPY' },
        { from: 'USD', to: 'XAU' },
        { from: 'EUR', to: 'SDR' },
    ];

    const handleQuickConvert = async (from: string, to: string) => {
        setFromCurrency(from);
        setToCurrency(to);
        const result = await convertMutation.mutateAsync({
            amount: parseFloat(amount) || 100,
            from,
            to,
            date: selectedDate,
        });
        setConversionHistory((prev) => [result, ...prev].slice(0, 20));
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Exchange Rate Test
                </h2>
                <p className="mt-1 text-gray-600 dark:text-gray-400">
                    Test currency conversion with circuit breaker protection
                </p>
            </div>

                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Conversion Controls */}
                    <div className="space-y-6">
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Convert Currency
                            </h2>

                            <div className="space-y-4">
                                <Input
                                    label="Amount"
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <Input
                                        label="From"
                                        value={fromCurrency}
                                        onChange={(e) =>
                                            setFromCurrency(e.target.value.toUpperCase())
                                        }
                                        placeholder="USD"
                                        maxLength={3}
                                    />
                                    <Input
                                        label="To"
                                        value={toCurrency}
                                        onChange={(e) =>
                                            setToCurrency(e.target.value.toUpperCase())
                                        }
                                        placeholder="EUR"
                                        maxLength={3}
                                    />
                                </div>

                                <Input
                                    label="Date (rates as of)"
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    max={getYesterday()}
                                />

                                <Button
                                    onClick={handleConvert}
                                    isLoading={convertMutation.isPending}
                                    disabled={convertMutation.isPending}
                                    className="w-full"
                                >
                                    Convert
                                </Button>
                            </div>
                        </div>

                        {/* Quick Conversions */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Quick Conversions
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {quickConversions.map(({ from, to }) => (
                                    <Button
                                        key={`${from}-${to}`}
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => handleQuickConvert(from, to)}
                                        disabled={convertMutation.isPending}
                                    >
                                        {from} → {to}
                                    </Button>
                                ))}
                            </div>
                            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                XAU = Gold, XAG = Silver, SDR = Special Drawing Rights
                            </p>
                        </div>
                    </div>

                    {/* Circuit State */}
                    <div className="space-y-6">
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Circuit Breaker State
                            </h2>
                            {isLoadingCircuit ? (
                                <div className="text-gray-500 dark:text-gray-400">Loading...</div>
                            ) : circuitState ? (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">
                                            Service
                                        </span>
                                        <span className="font-mono text-sm">
                                            {circuitState.service_name}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">
                                            State
                                        </span>
                                        <StateIndicator state={circuitState.state} />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">
                                            Failures
                                        </span>
                                        <span
                                            className={`font-mono ${
                                                circuitState.failure_count > 0
                                                    ? 'text-red-600 dark:text-red-400'
                                                    : ''
                                            }`}
                                        >
                                            {circuitState.failure_count}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">
                                            Last Failure
                                        </span>
                                        <span className="font-mono text-sm">
                                            {circuitState.last_failure_at
                                                ? new Date(
                                                      circuitState.last_failure_at,
                                                  ).toLocaleTimeString()
                                                : '-'}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-gray-500 dark:text-gray-400">
                                    No state available
                                </div>
                            )}
                            <div className="mt-4">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={handleReset}
                                    isLoading={resetMutation.isPending}
                                    disabled={resetMutation.isPending}
                                >
                                    Reset Circuit
                                </Button>
                            </div>
                        </div>

                        {/* Stored Rates */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <div className="mb-4 flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Stored Rates
                                </h2>
                                {storedRates && (
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {storedRates.date} ({storedRates.total} total)
                                    </span>
                                )}
                            </div>
                            {isLoadingRates ? (
                                <div className="text-gray-500 dark:text-gray-400">Loading...</div>
                            ) : (
                                <div className="max-h-64 overflow-y-auto">
                                    <StoredRatesTable items={storedRates?.items ?? []} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Conversion History */}
                <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Conversion History
                        </h2>
                        {conversionHistory.length > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConversionHistory([])}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                    {conversionHistory.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400">
                            No conversions made yet. Use the form above to test currency
                            conversion.
                        </p>
                    ) : (
                        <div className="max-h-96 space-y-3 overflow-y-auto">
                            {conversionHistory.map((result, index) => (
                                <ConversionResultCard key={index} result={result} />
                            ))}
                        </div>
                    )}
                </div>
        </div>
    );
}
